import { useState, useRef, useEffect } from "react";

/* eslint-disable camelcase */
export interface StreamEvent {
    action: string;
    user_id: string;
    thing_id?: string;
    timestamp_initiated: string;
    timestamp_received?: string;
    device_platform?: string;
    session_id?: string;
    customer?: string;
    context_environment?: string;
    [key: string]: unknown;
}
/* eslint-enable camelcase */

export interface UseEventStreamReturn {
    events: StreamEvent[];
    isConnected: boolean;
    secondsRemaining: number;
    start: (customer: string) => void;
    stop: () => void;
    clear: () => void;
    error: string | null;
}

const SESSION_SECONDS = 300;
const RECONNECT_MS = 4 * 60 * 1000; // reconnect before server-side 5-min TTL
const MAX_EVENTS = 1000;

export function useEventStream(wsBaseUrl: string | undefined): UseEventStreamReturn {
    const [events, setEvents] = useState<StreamEvent[]>([]);
    const [isConnected, setIsConnected] = useState(false);
    const [secondsRemaining, setSecondsRemaining] = useState(SESSION_SECONDS);
    const [error, setError] = useState<string | null>(null);

    const wsRef = useRef<WebSocket | null>(null);
    const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const customerRef = useRef<string>("");

    const clearTimer = () => {
        if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }
    };

    const clearReconnectTimer = () => {
        if (reconnectTimerRef.current) {
            clearTimeout(reconnectTimerRef.current);
            reconnectTimerRef.current = null;
        }
    };

    const connect = (customer: string) => {
        if (!wsBaseUrl) return;

        // Suppress onclose for previous WS during reconnect to avoid spurious state updates
        const prevWs = wsRef.current;
        if (prevWs) {
            prevWs.onclose = null;
            prevWs.close();
        }

        const url = `${wsBaseUrl}?customer=${encodeURIComponent(customer)}`;
        const ws = new WebSocket(url);
        wsRef.current = ws;

        ws.onopen = () => {
            setIsConnected(true);
            // Schedule silent reconnect before server-side TTL expires
            reconnectTimerRef.current = setTimeout(() => {
                connect(customerRef.current);
            }, RECONNECT_MS);
        };

        ws.onmessage = (event) => {
            try {
                const parsed: StreamEvent = JSON.parse(event.data);
                setEvents(prev => [parsed, ...prev].slice(0, MAX_EVENTS));
            } catch {
                // malformed message — ignore
            }
        };

        ws.onclose = (event) => {
            clearReconnectTimer();
            setIsConnected(false);
            wsRef.current = null;
            if (!event.wasClean) {
                setError("Disconnected unexpectedly.");
            }
        };

        ws.onerror = () => {
            setError("WebSocket connection error.");
        };
    };

    const stop = () => {
        clearTimer();
        clearReconnectTimer();
        if (wsRef.current) {
            wsRef.current.close();
            wsRef.current = null;
        }
        setIsConnected(false);
        setSecondsRemaining(SESSION_SECONDS);
    };

    const start = (customer: string) => {
        if (!wsBaseUrl) {
            setError("No WebSocket URL configured for this customer.");
            return;
        }
        setError(null);
        setSecondsRemaining(SESSION_SECONDS);
        customerRef.current = customer;

        clearTimer();
        let remaining = SESSION_SECONDS;
        timerRef.current = setInterval(() => {
            remaining -= 1;
            setSecondsRemaining(remaining);
            if (remaining <= 0) {
                stop();
            }
        }, 1000);

        connect(customer);
    };

    const clear = () => setEvents([]);

    const stopRef = useRef(stop);
    stopRef.current = stop;

    // Cleanup on unmount
    useEffect(() => () => stopRef.current(), []);

    return { events, isConnected, secondsRemaining, start, stop, clear, error };
}
